'use client';

import { useEffect, useRef, useState } from 'react';
import type {
  BaseComponentProps,
  ContentfulNavigationPatternElement,
  ContentfulNavigationType,
} from '@bedrock/core/contentful';
import { usePathname } from 'next/navigation';
import NavigationLevel, { type NavigationPageLevel } from './navigation-level';
import { useHash } from './use-hash';
import { collectDirectLinkPaths, flatNavigationLinks, isNavigationType } from './utils';

type DrawerContentProps = Pick<ContentfulNavigationPatternElement, 'mainNavigation'> &
  Pick<BaseComponentProps, 'dictionary' | 'locale'>;

export default function HamburgerDrawerContent({
  dictionary,
  mainNavigation,
  locale,
}: Readonly<DrawerContentProps>) {
  const pathname = usePathname();
  const hash = useHash();
  const [stack, setStack] = useState<ContentfulNavigationType[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const enter = (item: ContentfulNavigationType) => {
    setStack((prevState) => [...prevState, item]);
    requestAnimationFrame(() => {
      setActiveIndex((i) => i + 1);
    });
  };

  const back = () => {
    setActiveIndex((i) => Math.max(0, i - 1));
  };

  const rootItems = flatNavigationLinks(mainNavigation.navigation);
  const rootDirectPaths = collectDirectLinkPaths(rootItems);

  const levels: NavigationPageLevel[] = [
    {
      id: 'root',
      title: mainNavigation.title ?? 'Back',
      items: rootItems,
      utility: mainNavigation.secondaryNavigation ?? [],
    },
    ...stack.map((node) => ({
      id: node.sysId,
      title: isNavigationType(node.linkedPage)
        ? (node.linkedPage.title ?? 'Back')
        : (node.linkedPage?.label ?? 'Back'),
      items: [...flatNavigationLinks(node.navigation)],
      featuredLinks: node.secondaryNavigation,
    })),
  ];

  const stackLengthRef = useRef(stack.length);
  stackLengthRef.current = stack.length;

  useEffect(() => {
    if (activeIndex < stackLengthRef.current) {
      setStack((prev) => prev.slice(0, activeIndex));
    }
  }, [activeIndex]);

  return (
    <div className="patterns-navigation-hamburger-content h-full overflow-hidden">
      <div
        className="patterns-navigation-hamburger-track flex h-full transition-transform duration-300"
        style={{ transform: `translateX(-${activeIndex * 100}%)` }}
      >
        {levels.map((level, index) => (
          <NavigationLevel
            key={level.id}
            breadcrumbs={stack}
            dictionary={dictionary}
            hash={hash}
            isInView={index === activeIndex}
            level={level}
            locale={locale}
            onBack={back}
            onEnter={enter}
            parentExcludedPaths={rootDirectPaths}
            pathname={pathname}
            title={levels[index - 1]?.title}
          />
        ))}
      </div>
    </div>
  );
}
